import { googleMapsDistanceService } from '../services/googleMapsDistanceService';
import { roundToNearest5Minutes, roundToStepMinutes, addMinutes, parseBackendDate } from './timeUtils';
import type { Appointment } from '../components/timeline/types';
import type { Routemize_DTOs_AppointmentSummary } from '../api/models/Routemize_DTOs_AppointmentSummary';
import type { Routemize_DTOs_AppointmentListItem } from '../api/models/Routemize_DTOs_AppointmentListItem';

/**
 * A slot where a stop can fit in the schedule.
 * Fields match what StopSlotSelectionScreen expects (Routemize_DTOs_SlotSuggestionDTO shape).
 */
export interface StopSlot {
  id: string;
  suggestedDate: string;
  startTime: string;
  endTime: string;
  routeScore: number;
  isRecommended: boolean;
  previousAppointmentLocation: string;
  previousAppointmentDistanceMiles?: number;
  previousAppointmentTravelTimeMinutes?: number;
  previousAppointmentStartTime: string;
  previousAppointmentEndTime: string;
  nextAppointmentLocation: string;
  nextAppointmentDistanceMiles?: number;
  nextAppointmentTravelTimeMinutes?: number;
  nextAppointmentStartTime: string;
  nextAppointmentEndTime: string;
}

type TimePreference = 'Morning' | 'Afternoon' | 'Evening' | 'Flexible';

const TIME_PREFERENCE_RANGES: Record<TimePreference, { startHour: number; endHour: number }> = {
  Morning: { startHour: 6, endHour: 12 },
  Afternoon: { startHour: 12, endHour: 17 },
  Evening: { startHour: 17, endHour: 21 },
  Flexible: { startHour: 0, endHour: 24 },
};

/**
 * When "expand" mode is on, we try to surface at least one candidate per
 * time-of-day bucket within each gap so users can see morning / afternoon /
 * evening options on wide-open days. Anchor hours are set mid-bucket so they
 * feel natural (e.g. 9 AM, 1 PM, 5 PM).
 */
const TIME_OF_DAY_ANCHORS: Array<{ label: 'Morning' | 'Afternoon' | 'Evening'; hour: number }> = [
  { label: 'Morning', hour: 9 },
  { label: 'Afternoon', hour: 13 },
  { label: 'Evening', hour: 17 },
];

/** Candidate slots within the same gap that start within this window are considered duplicates. */
const CANDIDATE_DEDUPE_MINUTES = 45;

function toHHMM(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function toLocalDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Parse an HH:MM time string into a Date on a specific day (local time).
 */
function timeOnDate(date: Date, timeStr: string): Date {
  const [h, m] = timeStr.split(':').map(Number);
  const d = new Date(date);
  d.setHours(h, m, 0, 0);
  return d;
}

interface GapInfo {
  startTime: Date;
  endTime: Date;
  prevAppointment: Appointment | null;
  nextAppointment: Appointment | null;
}

interface MergedBusyBlock {
  startTime: Date;
  endTime: Date;
  /**
   * The appointment whose end-time bounds this merged busy block — i.e. the
   * one we route from when computing drive time for the next slot. When
   * multiple events overlap, this is the latest-ending member of the union.
   */
  endAnchor: Appointment;
  /**
   * The appointment whose start-time bounds this merged busy block — i.e.
   * the one that "starts" the block, used as the next-event anchor when
   * computing drive time INTO the slot before this block.
   */
  startAnchor: Appointment;
}

/**
 * Coalesce a sorted list of appointments into non-overlapping busy blocks.
 *
 * Required because long external "Busy" events (e.g. Google Calendar
 * all-day blocks) frequently overlap with internal appointments. Without
 * merging, a pairwise sort-by-start scan would treat the gap between two
 * shorter contained events as available time — even though a longer
 * surrounding event is still blocking. See the "Move to Another Time"
 * regression where suggested slots overlapped existing blocked time.
 */
function mergeBusyBlocks(sorted: Appointment[]): MergedBusyBlock[] {
  const merged: MergedBusyBlock[] = [];
  for (const apt of sorted) {
    const last = merged[merged.length - 1];
    // Only merge when the next event truly starts BEFORE the running max
    // end-time of the union so far. Touching at a boundary (start == end)
    // is treated as adjacent, not overlapping.
    if (last && apt.startDateTime.getTime() < last.endTime.getTime()) {
      if (apt.endDateTime.getTime() > last.endTime.getTime()) {
        last.endTime = apt.endDateTime;
        // The latest-ending member is the one whose location the user is
        // (presumed to be) driving from when this block ends.
        last.endAnchor = apt;
      }
      continue;
    }
    merged.push({
      startTime: apt.startDateTime,
      endTime: apt.endDateTime,
      startAnchor: apt,
      endAnchor: apt,
    });
  }
  return merged;
}

/**
 * Find gaps in a day's schedule where a stop could potentially fit.
 * Works with the timeline's Appointment objects (startDateTime/endDateTime are Date objects
 * parsed from the backend's local time strings).
 *
 * `nowOverride` lets callers pass a tenant-shifted "now" so the past-time
 * filter stays in the same frame as the appointment Dates. Without it the
 * default `new Date()` mixes browser-real-UTC with tenant-shifted Dates,
 * which truncates the available window incorrectly when the impersonating
 * admin lives in a different timezone than the tenant.
 */
function findGaps(
  appointments: Appointment[],
  date: Date,
  workingHoursStart: string,
  workingHoursEnd: string,
  nowOverride?: Date,
): GapInfo[] {
  // Filter to appointments on this specific date
  const sorted = [...appointments]
    .filter(a => a.startDateTime.toDateString() === date.toDateString())
    .sort((a, b) => a.startDateTime.getTime() - b.startDateTime.getTime());

  const busyBlocks = mergeBusyBlocks(sorted);

  const dayStart = timeOnDate(date, workingHoursStart);
  const dayEnd = timeOnDate(date, workingHoursEnd);

  // For today, don't show gaps in the past
  const now = nowOverride ?? new Date();
  const isToday = date.toDateString() === now.toDateString();
  const effectiveStart = isToday
    ? new Date(Math.max(dayStart.getTime(), now.getTime() + 15 * 60_000))
    : dayStart;

  // If effective start is already past day end, no gaps possible
  if (effectiveStart >= dayEnd) return [];

  const gaps: GapInfo[] = [];

  if (busyBlocks.length === 0) {
    gaps.push({ startTime: effectiveStart, endTime: dayEnd, prevAppointment: null, nextAppointment: null });
    return gaps;
  }

  // Gap before first busy block
  const firstStart = busyBlocks[0].startTime;
  if (effectiveStart < firstStart) {
    gaps.push({ startTime: effectiveStart, endTime: firstStart, prevAppointment: null, nextAppointment: busyBlocks[0].startAnchor });
  }

  // Gaps between busy blocks
  for (let i = 0; i < busyBlocks.length - 1; i++) {
    const currentEnd = busyBlocks[i].endTime;
    const nextStart = busyBlocks[i + 1].startTime;
    if (currentEnd < nextStart) {
      gaps.push({
        startTime: currentEnd,
        endTime: nextStart,
        prevAppointment: busyBlocks[i].endAnchor,
        nextAppointment: busyBlocks[i + 1].startAnchor,
      });
    }
  }

  // Gap after last busy block
  const lastBlock = busyBlocks[busyBlocks.length - 1];
  if (lastBlock.endTime < dayEnd) {
    gaps.push({ startTime: lastBlock.endTime, endTime: dayEnd, prevAppointment: lastBlock.endAnchor, nextAppointment: null });
  }

  return gaps;
}

/**
 * A simplified open gap with the coordinates/labels of the appointments that
 * bracket it. Used by Routy to find places that fit near existing stops
 * (proximity based on real appointments, not just the starting location).
 */
export interface OpenGap {
  date: Date;
  startTime: Date;
  endTime: Date;
  durationMinutes: number;
  prevCoords: { lat: number; lng: number } | null;
  nextCoords: { lat: number; lng: number } | null;
  prevLabel: string | null;
  nextLabel: string | null;
}

/**
 * Enumerate the open gaps in a day with bracketing-appointment context.
 * Thin public wrapper over the internal gap finder.
 */
export function findOpenGaps(
  appointments: Appointment[],
  date: Date,
  workingHoursStart: string,
  workingHoursEnd: string,
  nowOverride?: Date,
): OpenGap[] {
  const label = (apt: Appointment | null): string | null =>
    apt ? `${apt.startTime} ${apt.customerName || apt.type || 'stop'}`.trim() : null;
  const coords = (apt: Appointment | null) =>
    apt && apt.latitude != null && apt.longitude != null ? { lat: apt.latitude, lng: apt.longitude } : null;

  return findGaps(appointments, date, workingHoursStart, workingHoursEnd, nowOverride).map((g) => ({
    date,
    startTime: g.startTime,
    endTime: g.endTime,
    durationMinutes: Math.round((g.endTime.getTime() - g.startTime.getTime()) / 60_000),
    prevCoords: coords(g.prevAppointment),
    nextCoords: coords(g.nextAppointment),
    prevLabel: label(g.prevAppointment),
    nextLabel: label(g.nextAppointment),
  }));
}

/**
 * Estimate drive times from surrounding appointments to the stop address.
 */
async function estimateDriveTimes(
  stopCoords: { lat: number; lng: number },
  prevAppointment: Appointment | null,
  nextAppointment: Appointment | null,
  startingCoords?: { lat: number; lng: number },
): Promise<{
  driveFromMin: number | null;
  driveToMin: number | null;
  driveFromMiles: number | null;
  driveToMiles: number | null;
}> {
  let driveFromMin: number | null = null;
  let driveToMin: number | null = null;
  let driveFromMiles: number | null = null;
  let driveToMiles: number | null = null;

  const fromCoords = prevAppointment?.latitude && prevAppointment?.longitude
    ? { lat: prevAppointment.latitude, lng: prevAppointment.longitude }
    : startingCoords ?? null;

  const toCoords = nextAppointment?.latitude && nextAppointment?.longitude
    ? { lat: nextAppointment.latitude, lng: nextAppointment.longitude }
    : null;

  const promises: Promise<void>[] = [];

  if (fromCoords) {
    promises.push(
      googleMapsDistanceService.calculateDrivingDistance(fromCoords, stopCoords)
        .then(r => { if (r) { driveFromMin = r.duration; driveFromMiles = r.distance; } })
        .catch(() => {}),
    );
  }

  if (toCoords) {
    promises.push(
      googleMapsDistanceService.calculateDrivingDistance(stopCoords, toCoords)
        .then(r => { if (r) { driveToMin = r.duration; driveToMiles = r.distance; } })
        .catch(() => {}),
    );
  }

  await Promise.all(promises);
  return { driveFromMin, driveToMin, driveFromMiles, driveToMiles };
}

/**
 * Score a slot based on time preference match and route efficiency.
 */
function scoreSlot(
  slotStart: Date,
  timePreference: TimePreference,
  driveFromMin: number | null,
  driveToMin: number | null,
): number {
  let score = 50;

  // Time preference match (0-30 points)
  const range = TIME_PREFERENCE_RANGES[timePreference];
  const hour = slotStart.getHours();
  if (timePreference === 'Flexible') {
    score += 20;
  } else if (hour >= range.startHour && hour < range.endHour) {
    score += 30;
  } else {
    const distance = Math.min(Math.abs(hour - range.startHour), Math.abs(hour - range.endHour));
    score += Math.max(0, 20 - distance * 5);
  }

  // Route efficiency (0-20 points) — shorter total drive = better
  const totalDrive = (driveFromMin ?? 15) + (driveToMin ?? 15);
  if (totalDrive <= 10) score += 20;
  else if (totalDrive <= 20) score += 15;
  else if (totalDrive <= 30) score += 10;
  else if (totalDrive <= 45) score += 5;

  return Math.min(100, score);
}

function appointmentAddress(apt: Appointment | null, fallback: string): string {
  if (!apt) return fallback;
  return apt.address || apt.type || fallback;
}

/**
 * Find available slots for a stop on a single day.
 *
 * Slot generation modes (in order of precedence):
 *   1. `slotIntervalMinutes` set → emit every viable start time within each
 *      gap, aligned to that interval (15 / 30 / 60). This is the "show all
 *      available times" path used by Plan-a-Stop and the move-event picker.
 *   2. `expandSlotsAcrossDay` true → emit one earliest-viable slot plus
 *      morning / afternoon / evening anchor options (legacy behavior).
 *   3. Default → emit only the single earliest-viable slot per gap.
 */
export async function findStopSlotsForDay(params: {
  appointments: Appointment[];
  stopCoordinates: { lat: number; lng: number };
  stopDurationMinutes: number;
  date: Date;
  timePreference: TimePreference;
  workingHoursStart: string;
  workingHoursEnd: string;
  startingCoordinates?: { lat: number; lng: number };
  expandSlotsAcrossDay?: boolean;
  /**
   * When provided, every valid start within each gap is emitted at this
   * interval (e.g. 15, 30, 60). Takes precedence over `expandSlotsAcrossDay`.
   * Drive-time lookups still happen once per gap, so emitting many slots is
   * effectively free.
   */
  slotIntervalMinutes?: number;
  /**
   * Optional "now" Date in the same frame as the appointment Dates (i.e.
   * tenant-shifted). Pass to keep the past-time filter consistent when the
   * impersonating admin lives in a different timezone than the tenant.
   */
  nowOverride?: Date;
}): Promise<StopSlot[]> {
  const {
    appointments, stopCoordinates, stopDurationMinutes,
    date, timePreference, workingHoursStart, workingHoursEnd, startingCoordinates,
    expandSlotsAcrossDay = false,
    slotIntervalMinutes,
    nowOverride,
  } = params;

  const gaps = findGaps(appointments, date, workingHoursStart, workingHoursEnd, nowOverride);
  const durationMs = stopDurationMinutes * 60_000;
  const dateStr = toLocalDateStr(date);
  const startLabel = startingCoordinates ? 'Office / Home' : 'Start of Day';

  const slots: StopSlot[] = [];

  for (let i = 0; i < gaps.length; i++) {
    const gap = gaps[i];
    const gapMs = gap.endTime.getTime() - gap.startTime.getTime();

    // Quick check: if gap is too small even without drive times, skip
    if (gapMs < durationMs) continue;

    const drives = await estimateDriveTimes(stopCoordinates, gap.prevAppointment, gap.nextAppointment, startingCoordinates);

    const driveFromMs = (drives.driveFromMin ?? 0) * 60_000;
    const driveToMs = (drives.driveToMin ?? 0) * 60_000;
    const availableMs = gapMs - driveFromMs - driveToMs;

    if (availableMs < durationMs) continue;

    // Earliest usable start within the gap (after drive-in, rounded up to 5 min).
    const rawStopStart = new Date(gap.startTime.getTime() + driveFromMs);
    const earliestStart = roundToNearest5Minutes(rawStopStart, 'up');

    // Latest usable start that still leaves drive-out room before next anchor.
    const latestStart = new Date(
      gap.endTime.getTime() - driveToMs - stopDurationMinutes * 60_000,
    );

    if (latestStart.getTime() < earliestStart.getTime()) continue;

    // Build the list of candidate starts for this gap.
    const candidateStarts: Date[] = [];
    const pushCandidate = (candidate: Date) => {
      if (candidate.getTime() < earliestStart.getTime()) return;
      if (candidate.getTime() > latestStart.getTime()) return;
      // De-dupe: skip if within the collapse window of an already-added candidate.
      const tooClose = candidateStarts.some(
        (existing) => Math.abs(existing.getTime() - candidate.getTime()) < CANDIDATE_DEDUPE_MINUTES * 60_000,
      );
      if (tooClose) return;
      candidateStarts.push(candidate);
    };

    if (slotIntervalMinutes && slotIntervalMinutes > 0) {
      // "Show all available times" mode: emit every interval-aligned start
      // between earliest and latest viable. The aligned grid is rooted at
      // midnight so different gaps share a consistent ":00 / :15 / :30"
      // rhythm — a 60-min interval always reads as 8:00 / 9:00 / 10:00
      // rather than 8:35 / 9:35 / 10:35. The earliest viable start is only
      // included if it already lines up with the grid; off-grid earliest
      // values are skipped to keep the chip list legible.
      const interval = slotIntervalMinutes;
      let cursor = roundToStepMinutes(earliestStart, interval, 'up');
      while (cursor.getTime() <= latestStart.getTime()) {
        candidateStarts.push(new Date(cursor));
        cursor = addMinutes(cursor, interval);
      }
    } else {
      pushCandidate(earliestStart);

      if (expandSlotsAcrossDay) {
        for (const anchor of TIME_OF_DAY_ANCHORS) {
          const anchorDate = new Date(date);
          anchorDate.setHours(anchor.hour, 0, 0, 0);
          const snapped = roundToStepMinutes(anchorDate, 5, 'up');
          pushCandidate(snapped);
        }
        // Also include the latest viable slot (rounded DOWN) so end-of-day
        // options are represented for very wide gaps.
        const latestRounded = roundToStepMinutes(latestStart, 5, 'down');
        pushCandidate(latestRounded);
      }
    }

    candidateStarts.sort((a, b) => a.getTime() - b.getTime());

    for (let c = 0; c < candidateStarts.length; c++) {
      const stopStart = candidateStarts[c];
      const stopEnd = addMinutes(stopStart, stopDurationMinutes);

      // Double-check the slot fits (defensive — pushCandidate already enforces this).
      if (stopEnd.getTime() + driveToMs > gap.endTime.getTime()) continue;

      const score = scoreSlot(stopStart, timePreference, drives.driveFromMin, drives.driveToMin);

      slots.push({
        id: `stop-slot-${dateStr}-${i}-${c}`,
        suggestedDate: dateStr,
        startTime: toHHMM(stopStart),
        endTime: toHHMM(stopEnd),
        routeScore: score,
        isRecommended: false,
        previousAppointmentLocation: appointmentAddress(gap.prevAppointment, startLabel),
        previousAppointmentDistanceMiles: drives.driveFromMiles ?? undefined,
        previousAppointmentTravelTimeMinutes: drives.driveFromMin ?? undefined,
        previousAppointmentStartTime: gap.prevAppointment ? toHHMM(gap.prevAppointment.startDateTime) : toHHMM(gap.startTime),
        previousAppointmentEndTime: gap.prevAppointment ? toHHMM(gap.prevAppointment.endDateTime) : toHHMM(gap.startTime),
        nextAppointmentLocation: appointmentAddress(gap.nextAppointment, 'End of Day'),
        nextAppointmentDistanceMiles: drives.driveToMiles ?? undefined,
        nextAppointmentTravelTimeMinutes: drives.driveToMin ?? undefined,
        nextAppointmentStartTime: gap.nextAppointment ? toHHMM(gap.nextAppointment.startDateTime) : toHHMM(gap.endTime),
        nextAppointmentEndTime: gap.nextAppointment ? toHHMM(gap.nextAppointment.endDateTime) : toHHMM(gap.endTime),
      });
    }
  }

  return slots;
}

/**
 * Convert raw API timeline items to the Appointment shape the gap finder expects.
 * Used when fetching appointments for days other than the currently selected date.
 */
/**
 * Map the backend AppointmentStatus (enum name, or its numeric value from
 * some serializers) to the timeline's lowercase status union. Defaults to
 * 'scheduled' for unknown values. Carrying the REAL status matters: a past
 * appointment still marked Scheduled must never be presented as completed
 * (QA 86bavp7rz).
 */
export function mapBackendAppointmentStatus(status: unknown): Appointment['status'] {
  switch (String(status ?? '')) {
    case 'Completed':
    case '2':
      return 'complete';
    case 'Cancelled':
    case '3':
      return 'cancelled';
    case 'NoShow':
    case '4':
      return 'no-show';
    case 'OnTheWay':
    case '5':
      return 'on-the-way';
    case 'InProgress':
    case '6':
      return 'in-progress';
    default:
      return 'scheduled';
  }
}

export function timelineItemsToAppointments(
  items: Array<{ appointment?: Routemize_DTOs_AppointmentSummary; }>,
  timezone?: string,
): Appointment[] {
  return items
    .filter(item => !!item.appointment)
    .map(item => {
      const apt = item.appointment!;
      // `startTime`/`endTime` are the tenant-local wall-clock strings the backend already
      // converts to (no timezone indicator). `new Date()` parses them into the browser's
      // local Date fields, so the Date's LOCAL getters (getHours/getDate/etc.) end up
      // holding the tenant's wall-clock digits — this is the same "shifted" frame used by
      // `tenantShiftedNow`/working-hours boundaries elsewhere in the gap finder, and it's
      // required for `.getTime()` comparisons against those boundaries to line up
      // regardless of the browser's real timezone.
      //
      // Only fall back to the *Utc fields (true UTC instants, parsed via parseBackendDate)
      // when the local fields are missing — that's the one case where explicit `timeZone`
      // formatting/conversion is actually correct.
      const usingLocalStart = !!apt.startTime;
      const usingLocalEnd = !!apt.endTime;
      const startDate = usingLocalStart ? new Date(apt.startTime!) : parseBackendDate(apt.startTimeUtc) ?? new Date(NaN);
      const endDate = usingLocalEnd ? new Date(apt.endTime!) : parseBackendDate(apt.endTimeUtc) ?? new Date(NaN);
      const startTzOptions: Intl.DateTimeFormatOptions = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        // Never re-apply `timeZone` to an already-shifted local Date — doing so double-
        // converts and shows a time off by the browser/tenant offset (e.g. an admin
        // viewing from a different timezone than the company sees the wrong stop time).
        ...(!usingLocalStart && timezone ? { timeZone: timezone } : {}),
      };
      const endTzOptions: Intl.DateTimeFormatOptions = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        ...(!usingLocalEnd && timezone ? { timeZone: timezone } : {}),
      };

      return {
        id: apt.id || '',
        startTime: startDate.toLocaleTimeString('en-US', startTzOptions),
        endTime: endDate.toLocaleTimeString('en-US', endTzOptions),
        startDateTime: startDate,
        endDateTime: endDate,
        date: startDate,
        customerName: apt.contactName || '',
        address: apt.address || [apt.addressLine1, apt.city, apt.state].filter(Boolean).join(', '),
        distance: '',
        status: mapBackendAppointmentStatus(apt.status),
        initials: '',
        // An ad-hoc stop's label is its type, as in the timeline's `useAppointments` (D6). Note text is not mapped:
        // `AppointmentSummary.notes` is computed server-side and is a label marker for stops (F2).
        type: apt.stopLabel?.trim() || apt.bookingFormName || apt.title || '',
        ...(apt.stopLabel?.trim() ? { isAdHocStop: true } : {}),
        latitude: apt.latitude ?? undefined,
        longitude: apt.longitude ?? undefined,
        source: apt.source as unknown as Appointment['source'],
      };
    });
}

/**
 * Convert AppointmentListItem results (from /api/app/appointment/search) to Appointment shape.
 * Single search call returns all appointments in a date range.
 *
 * External calendar events (Google / Outlook / Zoom "Busy" blocks) ARE
 * included so the gap finder treats them as blockers. Filtering them out
 * here previously caused the slot picker to suggest times that overlapped
 * existing calendar events.
 */
export function searchResultsToAppointments(
  items: Routemize_DTOs_AppointmentListItem[],
): Appointment[] {
  return items
    .map(item => {
      // Use local time (already converted by backend), fallback to UTC
      const startDate = new Date(item.meetingStartTime || item.meetingStartTimeUtc || '');
      const endDate = new Date(item.meetingEndTime || item.meetingEndTimeUtc || '');

      return {
        id: item.id || '',
        startTime: startDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
        endTime: endDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
        startDateTime: startDate,
        endDateTime: endDate,
        date: startDate,
        customerName: [item.contactFirstName, item.contactLastName].filter(Boolean).join(' ') || '',
        address: [item.addressLine1, item.city, item.state].filter(Boolean).join(', '),
        distance: '',
        status: 'scheduled' as const,
        initials: '',
        type: item.bookingFormName || item.eventName || '',
        latitude: item.latitude ?? undefined,
        longitude: item.longitude ?? undefined,
      };
    });
}

/**
 * Check if a given date still has time available within working hours.
 *
 * `nowOverride` lets callers pass a tenant-shifted "now" so the comparison
 * matches the frame of the timeline's appointment Dates (avoids treating
 * the day as already over for an EDT admin viewing a PT tenant's morning).
 */
export function isDayAvailable(date: Date, workingHoursEnd: string, nowOverride?: Date): boolean {
  const now = nowOverride ?? new Date();
  if (date.toDateString() !== now.toDateString()) return true; // future day is always available
  const dayEnd = timeOnDate(date, workingHoursEnd);
  // Need at least 15 minutes remaining
  return now.getTime() + 15 * 60_000 < dayEnd.getTime();
}

/**
 * Find slots across multiple days. Sorts all results by score descending
 * and marks the top one as recommended.
 */
export async function findStopSlotsMultiDay(params: {
  daySchedules: Array<{
    date: Date;
    appointments: Appointment[];
    workingHoursStart: string;
    workingHoursEnd: string;
  }>;
  stopCoordinates: { lat: number; lng: number };
  stopDurationMinutes: number;
  timePreference: TimePreference;
  startingCoordinates?: { lat: number; lng: number };
  expandSlotsAcrossDay?: boolean;
  /** See `findStopSlotsForDay.slotIntervalMinutes`. */
  slotIntervalMinutes?: number;
  /** See `findStopSlotsForDay.nowOverride`. */
  nowOverride?: Date;
}): Promise<StopSlot[]> {
  const {
    daySchedules,
    stopCoordinates,
    stopDurationMinutes,
    timePreference,
    startingCoordinates,
    expandSlotsAcrossDay = false,
    slotIntervalMinutes,
    nowOverride,
  } = params;

  const allSlots: StopSlot[] = [];

  for (const day of daySchedules) {
    const daySlots = await findStopSlotsForDay({
      appointments: day.appointments,
      stopCoordinates,
      stopDurationMinutes,
      date: day.date,
      timePreference,
      workingHoursStart: day.workingHoursStart,
      workingHoursEnd: day.workingHoursEnd,
      startingCoordinates,
      expandSlotsAcrossDay,
      slotIntervalMinutes,
      nowOverride,
    });
    allSlots.push(...daySlots);
  }

  // Sort by score descending, mark top as recommended
  allSlots.sort((a, b) => b.routeScore - a.routeScore);
  if (allSlots.length > 0) {
    allSlots[0].isRecommended = true;
  }

  return allSlots;
}
